import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';

const EMPTY = { name: '', email: '', company: '', company_url: '', message: '', website: '' };
const input = 'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/30';

const Field = ({ label, children }) => (
  <label className="block text-sm font-medium text-slate-700">{label}<div className="mt-1">{children}</div></label>
);

export const FeaturedEnquiryDialog = ({ open, onClose }) => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const close = () => { onClose(); setTimeout(() => setSent(false), 200); };

  const submit = async (e) => {
    e.preventDefault();
    if (f.name.trim().length < 2 || f.company.trim().length < 2 || !/^\S+@\S+\.\S+$/.test(f.email.trim())) {
      toast.error('Please add your name, company and a valid email'); return;
    }
    setBusy(true);
    try {
      await axios.post('/featured-enquiries', { ...f, email: f.email.trim() });
      setSent(true); setF(EMPTY);
    } catch (err) {
      toast.error(err.response?.status === 429 ? 'Too many requests. Please try again later.' : 'Could not send. Please try again.');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="sm:max-w-md" data-testid="featured-enquiry-dialog">
        {sent ? (
          <div className="py-6 text-center" data-testid="featured-enquiry-success">
            <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-600" />
            <h3 className="mt-3 text-lg font-bold text-slate-900">Thanks, we'll be in touch</h3>
            <p className="mt-1 text-sm text-slate-600">Our team will email you about featured placement options.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3" noValidate>
            <DialogHeader>
              <DialogTitle>Get featured on ClinicIQ</DialogTitle>
              <DialogDescription>Put your solution in front of clinics on our homepage. Tell us a little about you and we'll follow up.</DialogDescription>
            </DialogHeader>
            <Field label="Your name *"><input className={input} value={f.name} onChange={set('name')} data-testid="featured-enquiry-name" /></Field>
            <Field label="Work email *"><input type="email" className={input} value={f.email} onChange={set('email')} data-testid="featured-enquiry-email" /></Field>
            <Field label="Company *"><input className={input} value={f.company} onChange={set('company')} data-testid="featured-enquiry-company" /></Field>
            <Field label="Company website"><input className={input} value={f.company_url} onChange={set('company_url')} data-testid="featured-enquiry-url" /></Field>
            <Field label="Anything we should know?"><textarea rows={3} className={input} value={f.message} onChange={set('message')} data-testid="featured-enquiry-message" /></Field>
            <input type="text" tabIndex={-1} autoComplete="off" className="hidden" value={f.website} onChange={set('website')} aria-hidden="true" />
            <button type="submit" disabled={busy} data-testid="featured-enquiry-submit"
              className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-orange-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-orange-500 disabled:opacity-60">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Send enquiry
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
